import type { UserType } from '@prisma/client';
import { FlowRoleSchema, type FlowRole } from './role';

/**
 * Role View Switcher — single module answering "which label does this team
 * carry", "which UserType does it map to" and "where does it sort".
 * Everything downstream (API payloads, the frontend nav switcher and
 * `useRoleView`) imports from here;
 * nothing else branches on team names.
 */

interface TeamViewEntry {
  /** Switcher display label. */
  label: string;
  /** The Prisma `UserType` this team's real users carry. */
  userType: UserType;
  /** Display rank in the team picker (lowest first). */
  order: number;
  /** Whether Flow's tab bar shows the Signals tab. */
  showsFlowSignalsTab: boolean;
}

/**
 * Per-team registry. `Record<FlowRole, ...>` makes a missing entry a type
 * error if `FlowRole` ever grows a new member.
 *
 * Labels live here, not in `USER_TYPE_INFO` — that registry's POST_SALES
 * `shortLabel` is 'EGS', but the PRD specifies 'GS' for this switcher.
 */
export const TEAM_VIEW_REGISTRY: Record<FlowRole, TeamViewEntry> = {
  claydr: { label: 'ClayDR', userType: 'SDR', order: 0, showsFlowSignalsTab: true },
  gtme: { label: 'GTME', userType: 'PRE_SALES', order: 1, showsFlowSignalsTab: true },
  gs: { label: 'GS', userType: 'POST_SALES', order: 2, showsFlowSignalsTab: false },
};

/** Team picker display order (ClayDR, GTME, GS) — the registry's `order`, not `FlowRoleSchema`'s enum order. */
export const TEAM_DISPLAY_ORDER: readonly FlowRole[] = [...FlowRoleSchema.options].sort(
  (a, b) => TEAM_VIEW_REGISTRY[a].order - TEAM_VIEW_REGISTRY[b].order,
);

/**
 * The role a rep with no team effectively sees. Invariant: its layout defaults
 * all equal `FLOW_LAYOUT_FALLBACK_ID` (pinned by a registry test), so a rep
 * with no team and a ClayDR rep render the same layouts.
 */
export const DEFAULT_UI_VIEW: FlowRole = 'claydr';

export function teamLabel(team: FlowRole): string {
  return TEAM_VIEW_REGISTRY[team].label;
}

/** Whether Flow shows the Signals tab for the effective team; no team reads as `DEFAULT_UI_VIEW`. */
export function showsFlowSignalsTab(team: FlowRole | null): boolean {
  return TEAM_VIEW_REGISTRY[team ?? DEFAULT_UI_VIEW].showsFlowSignalsTab;
}

/** The `UserType` a team's real users carry — the inverse of `flowRoleFromUserType`. */
export function userTypeForTeam(team: FlowRole): UserType {
  return TEAM_VIEW_REGISTRY[team].userType;
}

/**
 * `User.prefViewAsRole` is a free-text DB column (trust boundary): an
 * unknown or absent value degrades to "no lens" rather than throwing.
 */
export function parseStoredViewAsRole(value: unknown): FlowRole | null {
  const parsed = FlowRoleSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export interface ApplyLensInput<U extends string | null> {
  realUserType: U;
  realFlowRole: FlowRole | null;
  /** Stored lens; null = no lens. */
  viewAsRole: FlowRole | null;
}

export interface AppliedLens<U extends string | null> {
  /** EFFECTIVE userType (the lensed team's UserType when a lens is set). */
  userType: U | UserType;
  /** EFFECTIVE team. */
  flowRole: FlowRole | null;
  /** True only when the lens differs from the real team. */
  viewingAs: boolean;
}

/**
 * Derives the EFFECTIVE lens fields from a rep's real team and stored lens
 * prefs. The one rule set behind `/api/me` (`deriveUserControls`) and the
 * client's `useRoleView`, so they cannot drift. For response payloads only —
 * never permissions or ownership queries (PRD R7).
 */
export function applyLens<U extends string | null>(input: ApplyLensInput<U>): AppliedLens<U> {
  const { realUserType, realFlowRole, viewAsRole } = input;
  const flowRole = viewAsRole ?? realFlowRole;
  return {
    userType: viewAsRole !== null ? userTypeForTeam(viewAsRole) : realUserType,
    flowRole,
    viewingAs: viewAsRole !== null && viewAsRole !== realFlowRole,
  };
}
